import {
    booleanAttribute,
    Component,
    computed,
    forwardRef,
    HostAttributeToken,
    inject,
    input,
    OnInit,
    signal,
} from '@angular/core';
import {
    ControlValueAccessor,
    FormControl,
    NG_VALUE_ACCESSOR,
    ReactiveFormsModule,
} from '@angular/forms';
import { Ng2TelInputDirective } from '@shared/ng2-tel-input/directives/ng2-tel-input.directive';
import { derivedAsync } from 'ngxtension/derived-async';
import { allEventsObservable } from 'ngxtension/form-events';

@Component({
    selector: 'app-custom-input',
    templateUrl: './custom-input.component.html',
    imports: [ReactiveFormsModule, Ng2TelInputDirective],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CustomInputComponent),
            multi: true,
        },
    ],
})
export class CustomInputComponent implements OnInit, ControlValueAccessor {
    formControlName =
        inject(new HostAttributeToken('formControlName'), { optional: true }) ??
        '';

    placeholder = input('');
    type = input('text');
    required = input(false, { transform: booleanAttribute });
    email = input(false, { transform: booleanAttribute });
    label = input('');
    isDarkMode = input(false, { transform: booleanAttribute });
    editable = input(true, { transform: booleanAttribute });
    control = input<FormControl>(new FormControl());

    // A FormControl mutates in place, so reading it in the template alone never re-renders.
    private controlState = derivedAsync(() =>
        allEventsObservable(this.control())
    );
    /** Errors to display, which is only once the field has been touched. */
    protected errors = computed(() =>
        this.controlState()?.touched ? this.control().errors : null
    );
    protected disabled = computed(
        () => this.controlState()?.status === 'DISABLED'
    );

    private onChange = (value: string) => {};
    private onTouched = () => {};
    // writeValue is driven by the forms API, so this has to notify on its own.
    internalValue = signal('');

    ngOnInit(): void {
        if (!this.editable()) {
            this.control().disable();
        }
    }

    onInputChange(event: Event): void {
        const value = (event.target as HTMLInputElement).value;
        this.internalValue.set(value);
        this.onChange(value);
    }

    onInputBlur(): void {
        this.onTouched();
    }

    writeValue(value: string | null): void {
        this.internalValue.set(value || '');
    }

    registerOnChange(fn: (value: string) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        if (isDisabled) {
            this.control().disable();
        } else {
            this.control().enable();
        }
    }
}
